import { useContext, useState } from 'react';

import { authContext } from '../../contexts/AuthContext';
import { checkLogoFile, deleteVenueLogo, forgetLocalVenueLogo, uploadVenueLogo } from '../VenueLogo';

// Upload and removal of a player's venue logo, shared by the control panel and the TV's
// idle screen. The server pushes the changed player to every screen showing it, so the
// new logo arrives through the usual live update; `onChanged` is only for a caller that
// wants the new URL at once.
export const useVenueLogoEditor = (
  player: { id: number; playerIdentifier: string } | undefined,
  onChanged?: (logoUrl: string | null) => void,
) => {
  const context = useContext(authContext);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [tone, setTone] = useState<'ok' | 'bad'>('ok');

  const say = (nextTone: 'ok' | 'bad', text?: string) => {
    setTone(nextTone);
    setMessage(text);
  };

  const upload = async (file: File) => {
    if (!player) return;
    say('ok');
    setBusy(true);
    try {
      const problem = await checkLogoFile(file);
      if (problem) {
        say('bad', problem);
        return;
      }
      const response = await uploadVenueLogo(player.id, file, context.auth.token);
      await forgetLocalVenueLogo(player.playerIdentifier);
      if (onChanged) onChanged(response.logoUrl);
      say('ok', 'Logo saved. Every screen showing this player will use it.');
    } catch (error) {
      say('bad', error.message || 'The logo could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!player) return;
    say('ok');
    setBusy(true);
    try {
      await deleteVenueLogo(player.id, context.auth.token);
      await forgetLocalVenueLogo(player.playerIdentifier);
      if (onChanged) onChanged(null);
      say('ok', 'Logo removed. The screen shows the Jookable logo.');
    } catch (error) {
      say('bad', error.message || 'The logo could not be removed.');
    } finally {
      setBusy(false);
    }
  };

  return { busy, message, tone, upload, remove };
};
